import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { analysesApi } from '../../features/analyses/api'
import { useAnalysisStatus } from '../../features/analyses/hooks/useAnalysisStatus'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Alert from '../../components/common/Alert'
import Button from '../../components/common/Button'
import AnalysisStatusBadge from '../../features/analyses/components/AnalysisStatusBadge'
import StepProgress from '../../features/analyses/components/StepProgress'
import SynchroStepInputRouter from '../../features/analyses/components/synchro/StepInputRouter'
import VolumeFigureStepInputRouter from '../../features/analyses/components/volume-figure/StepInputRouter'

/**
 * Analysis Detail Page
 * Shows full analysis with steps, handles user input, polls for status
 */
function AnalysisDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [starting, setStarting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [canceling, setCanceling] = useState(false)

  // Enable polling only for active statuses
  const shouldPoll =
    analysis && (analysis.status === 'processing' || analysis.status === 'awaiting_input')
  const { status: currentStepStatus } = useAnalysisStatus(id, shouldPoll)

  const loadAnalysis = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await analysesApi.get(id)
      setAnalysis(response.data)
    } catch (err) {
      console.error('Error loading analysis:', err)
      setError(err.response?.data?.detail || 'Failed to load analysis')
      if (err.response?.status === 404) {
        toast.error('Analysis not found')
        setTimeout(() => navigate('/analyses'), 2000)
      }
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  // Load analysis on mount
  useEffect(() => {
    loadAnalysis()
  }, [loadAnalysis])

  // Update analysis when polling detects changes
  useEffect(() => {
    if (currentStepStatus && analysis) {
      // If the step or status has changed, reload the full analysis to get updated step data
      if (
        currentStepStatus.current_step !== analysis.current_step ||
        currentStepStatus.analysis_status !== analysis.status
      ) {
        loadAnalysis()
      }
    }
    // Note: We intentionally don't include 'analysis' in dependencies to avoid infinite loop
    // The effect should only run when currentStepStatus changes from polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepStatus, loadAnalysis])

  const handleStart = async () => {
    try {
      setStarting(true)
      await analysesApi.start(id)
      toast.success('Analysis started')
      // Reload to get updated state
      await loadAnalysis()
    } catch (err) {
      console.error('Error starting analysis:', err)
      toast.error(err.response?.data?.error || 'Failed to start analysis')
    } finally {
      setStarting(false)
    }
  }

  const handleSubmitStepInput = async (inputData) => {
    try {
      setSubmitting(true)
      await analysesApi.submitStep(id, inputData)
      toast.success('Input submitted successfully')
      // Reload to get updated state
      await loadAnalysis()
    } catch (err) {
      console.error('Error submitting step input:', err)
      toast.error(err.response?.data?.error || 'Failed to submit input')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this analysis?')) {
      return
    }

    try {
      setCanceling(true)
      await analysesApi.cancel(id)
      toast.success('Analysis cancelled')
      await loadAnalysis()
    } catch (err) {
      console.error('Error canceling analysis:', err)
      toast.error(err.response?.data?.error || 'Failed to cancel analysis')
    } finally {
      setCanceling(false)
    }
  }

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await analysesApi.downloadFile(fileId)

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()

      // Cleanup
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Download started')
    } catch (err) {
      console.error('Error downloading file:', err)
      toast.error(err.response?.data?.error || 'Failed to download file')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !analysis) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error" message={error || 'Analysis not found'} />
      </div>
    )
  }

  // Get current step from analysis.steps array (has full data) or fall back to polling data
  const currentStep =
    analysis.steps?.find((s) => s.step_number === analysis.current_step) ||
    currentStepStatus?.step ||
    null
  const showStartButton = analysis.status === 'configuring' && analysis.current_step === 0
  const showCancelButton = analysis.status === 'processing' || analysis.status === 'awaiting_input'
  const showUserInputForm = analysis.status === 'awaiting_input' && currentStep

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/analyses"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Analyses
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{analysis.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Project: {analysis.project_name} • Created by {analysis.created_by_email}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <AnalysisStatusBadge status={analysis.status} />
            {showCancelButton && (
              <Button variant="danger" onClick={handleCancel} disabled={canceling}>
                {canceling ? 'Canceling...' : 'Cancel'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Step Progress */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress</h2>
        <StepProgress
          currentStep={analysis.current_step}
          steps={analysis.steps || []}
          analysisType={analysis.analysis_type}
        />
      </div>

      {/* Start button (for new analyses) */}
      {showStartButton && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Ready to Start</h3>
              <p className="mt-1 text-sm text-gray-600">
                Click the button to begin processing this analysis
              </p>
            </div>
            <Button variant="primary" onClick={handleStart} disabled={starting}>
              {starting ? 'Starting...' : 'Start Analysis'}
            </Button>
          </div>
        </div>
      )}

      {/* Current step - User Input Required */}
      {showUserInputForm && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Step {currentStep.step_number}: User Input Required
          </h2>
          {analysis.analysis_type === 'synchro_parser' && (
            <SynchroStepInputRouter
              analysis={analysis}
              step={currentStep}
              onSubmit={handleSubmitStepInput}
              loading={submitting}
            />
          )}
          {analysis.analysis_type === 'volume_figure_generator' && (
            <VolumeFigureStepInputRouter
              analysis={analysis}
              step={currentStep}
              onSubmit={handleSubmitStepInput}
              loading={submitting}
            />
          )}
          {analysis.analysis_type !== 'synchro_parser' &&
            analysis.analysis_type !== 'volume_figure_generator' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  No input router configured for analysis type: {analysis.analysis_type}
                </p>
              </div>
            )}
        </div>
      )}

      {/* Processing indicator */}
      {analysis.status === 'processing' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Processing Step {analysis.current_step}...
              </p>
              <p className="text-xs text-blue-700 mt-0.5">
                This may take a few moments. The page will update automatically.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Completed state */}
      {analysis.status === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-900">Analysis Completed</p>
              <p className="text-xs text-green-700 mt-0.5">All steps finished successfully</p>
            </div>
          </div>
        </div>
      )}

      {/* Failed state */}
      {analysis.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-900">Analysis Failed</p>
              {currentStep?.error_message && (
                <p className="text-xs text-red-700 mt-0.5">{currentStep.error_message}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Input Files */}
      {analysis.analysis_files && analysis.analysis_files.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Input Files</h2>
          <ul className="space-y-2">
            {analysis.analysis_files
              .filter((af) => af.role === 'input')
              .map((af) => (
                <li key={af.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {af.file.original_filename}
                  <span className="text-xs text-gray-500">
                    ({(af.file.file_size / 1024).toFixed(1)} KB)
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Output Files */}
      {analysis.analysis_files &&
        analysis.analysis_files.filter((af) => af.role === 'output').length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Output Files</h2>
            {analysis.analysis_files.filter((af) => af.role === 'output').length === 0 ? (
              <p className="text-gray-500 text-sm">No output files generated yet.</p>
            ) : (
              <div className="space-y-3">
                {/* JSON Output (Step 4) */}
                {analysis.analysis_files
                  .filter((af) => af.role === 'output' && af.step_number === 4)
                  .map((af) => (
                    <div
                      key={af.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="w-8 h-8 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                        </svg>
                        <div>
                          <p className="font-medium text-gray-900">{af.file.original_filename}</p>
                          <p className="text-sm text-gray-500">
                            JSON Data • {formatFileSize(af.file.file_size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(af.file.id, af.file.original_filename)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Download JSON
                      </button>
                    </div>
                  ))}

                {/* Excel Output (Step 5) */}
                {analysis.analysis_files
                  .filter((af) => af.role === 'output' && af.step_number === 5)
                  .map((af) => (
                    <div
                      key={af.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                        </svg>
                        <div>
                          <p className="font-medium text-gray-900">{af.file.original_filename}</p>
                          <p className="text-sm text-gray-500">
                            Excel Spreadsheet • {formatFileSize(af.file.file_size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(af.file.id, af.file.original_filename)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Download Excel
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

      {/* Step Details */}
      {analysis.steps && analysis.steps.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step Details</h2>
          <div className="space-y-4">
            {analysis.steps.map((step) => (
              <details
                key={step.id}
                className="border border-gray-200 rounded-lg"
                open={step.step_number === analysis.current_step}
              >
                <summary className="cursor-pointer p-4 hover:bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">
                      Step {step.step_number}: {step.step_type}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        step.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : step.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : step.status === 'processing'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {step.status}
                    </span>
                  </div>
                </summary>
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                  <div className="text-sm space-y-2">
                    {step.started_at && (
                      <div>
                        <span className="font-medium">Started:</span>{' '}
                        {new Date(step.started_at).toLocaleString()}
                      </div>
                    )}
                    {step.completed_at && (
                      <div>
                        <span className="font-medium">Completed:</span>{' '}
                        {new Date(step.completed_at).toLocaleString()}
                      </div>
                    )}
                    {step.error_message && (
                      <div className="text-red-600">
                        <span className="font-medium">Error:</span> {step.error_message}
                      </div>
                    )}
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalysisDetailPage
